import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { filter, take } from 'rxjs/operators';

import { enter } from 'src/app/resources/animations';
import { Log } from 'src/app/utils/log';
import { RootState } from 'src/app/reducers';
import { dispatchInfoMessage } from 'src/app/actions/core.actions';
import { Visibility, VisibilityType } from 'src/app/models/create-bookmark-problem-list';
import { initializeUpdateBookmarkProblemListState, updateBookmarkProblemList } from 'src/app/actions/bookmark-problem-list.actions';
import { getUpdateBookmarkProblemListResult } from 'src/app/selectors/bookmark-problem-list.selectors';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UpdateBookmarkProblemListRequest } from 'src/app/models/update-bookmark-problem-list';

export interface ChangeBookmarkProblemListDialogData {
  id: string;
  listName: string;
  visibility: Visibility;
}

@Component({
  selector: 'app-change-bookmark-problem-list-dialog',
  templateUrl: './change-bookmark-problem-list-dialog.component.html',
  styleUrls: ['./change-bookmark-problem-list-dialog.component.scss'],
  animations: [enter]
})
export class ChangeBookmarkProblemListDialogComponent implements OnInit, OnDestroy {
  updateBookmarkProblemListForm: UntypedFormGroup;
  dialogTitle: string;
  bookmarkProblemListUpdating = false;
  errorMessage = '';
  private LOG_SOURCE = this.constructor.name;

  protected readonly VisibilityType = VisibilityType;

  constructor(
    private store: Store<RootState>,
    private dialogRef: MatDialogRef<ChangeBookmarkProblemListDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ChangeBookmarkProblemListDialogData
  ) {}

  ngOnInit() {
    this.dialogTitle = 'お気に入りリストの編集';

    this.setUpForms();
  }

  ngOnDestroy() {
    this.store.dispatch(initializeUpdateBookmarkProblemListState());
  }

  updateBookmarkProblemList() {
    if (this.updateBookmarkProblemListForm.invalid) {
      this.updateBookmarkProblemListForm.markAllAsTouched();
      return;
    }

    if (!this.data.id) {
      Log.warn(this.LOG_SOURCE, `dialog.data(お気に入りリストのid)が設定されていません: `, this.data.id);
      return;
    }

    Log.debug(this.LOG_SOURCE, 'お気に入りリストを編集します');
    this.disableForms();
    this.errorMessage = '';

    const name: string = this.updateBookmarkProblemListForm.get('listName').value;
    const visibility: Visibility = this.updateBookmarkProblemListForm.get('visibility').value;
    const request: UpdateBookmarkProblemListRequest = {
      id: this.data.id,
      name,
      visibility
    };

    this.store.dispatch(updateBookmarkProblemList(request));

    this.store
      .select(getUpdateBookmarkProblemListResult)
      .pipe(
        filter(it => it != null),
        take(1)
      )
      .subscribe(result => {
        this.store.dispatch(initializeUpdateBookmarkProblemListState());
        Log.debug(this.LOG_SOURCE, `お気に入りリストの編集結果: `, result);
        if (result.success) {
          const message = `${name} を編集しました`;
          this.dialogRef.close(result);
          this.store.dispatch(
            dispatchInfoMessage({
              message
            })
          );
          return;
        }

        Log.warn(this.LOG_SOURCE, `お気に入りリストの編集エラー: err.code: ${result.error ? result.error.code : 'none'}`, result.error);
        this.enableForms();
        this.errorMessage = result.error ? `[${result.error.code}] ${result.error.message}` : 'エラーが発生しました';
      });
  }

  cancel() {
    this.dialogRef.close(false);
  }

  // set ups or private method ---------------------------------------------------------------
  private setUpForms() {
    this.updateBookmarkProblemListForm = new FormGroup({
      listName: new FormControl(this.data.listName, [Validators.required, Validators.maxLength(50)]),
      visibility: new FormControl(this.data.visibility)
    });
  }

  private disableForms() {
    Object.keys(this.updateBookmarkProblemListForm.controls).forEach(ctrlName => {
      this.updateBookmarkProblemListForm.get(ctrlName).disable();
    });
    this.bookmarkProblemListUpdating = true;
  }

  private enableForms() {
    Object.keys(this.updateBookmarkProblemListForm.controls).forEach(ctrlName => {
      this.updateBookmarkProblemListForm.get(ctrlName).enable();
    });
    this.bookmarkProblemListUpdating = false;
  }
}
